// The breadcrumb names the person whose submission is currently open. That name must
// belong to the submission in the address bar: showing the previously opened student
// would invite grading the wrong person's work. The name is therefore kept per
// submission and only ever read back with the id taken from the route. It lives in
// memory on purpose, so it can never outlive the navigation that produced it.
const namesBySubmission: Record<string, string> = {};

// A page that loads its submission first and only then knows the name would otherwise
// write into a store nobody watches: the breadcrumb has already rendered by then and
// would stay empty until some unrelated update happens to redraw it.
type Listener = () => void;
const listeners = new Set<Listener>();

export const subscribeRevisionUser = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Submissions without a matriculation number are only identified by their token.
export const displayNameOfUser = (user: any): string => {
  if (!user) return "";
  if (user.matriculationNumber) {
    return user.givenName + " " + user.familyName + " (" + user.matriculationNumber + ")";
  }
  return user.token ?? "";
};

export const setRevisionUser = (submissionId: any, name: string) => {
  if (submissionId === undefined || submissionId === null || name === "") return;
  const key = submissionId.toString();
  if (namesBySubmission[key] === name) return;
  namesBySubmission[key] = name;
  listeners.forEach((listener) => listener());
};

export const getRevisionUser = (submissionId: any): string => {
  if (submissionId === undefined || submissionId === null) return "";
  return namesBySubmission[submissionId.toString()] ?? "";
};

// The exercise has the same problem as the person: the breadcrumb read its name from
// localStorage, which is only refreshed on the submission list. Opening a revision directly, or
// switching to a different exercise, therefore left the previously visited name standing, so the
// trail claimed a correction belonged to an exercise it had nothing to do with. Kept per
// submission and next to the name, because both are answers to the same question: what is open?
const exercisesBySubmission: Record<string, string> = {};

export const setRevisionExercise = (submissionId: any, name: string) => {
  if (submissionId === undefined || submissionId === null || !name) return;
  const key = submissionId.toString();
  if (exercisesBySubmission[key] === name) return;
  exercisesBySubmission[key] = name;
  listeners.forEach((listener) => listener());
};

export const getRevisionExercise = (submissionId: any): string => {
  if (submissionId === undefined || submissionId === null) return "";
  return exercisesBySubmission[submissionId.toString()] ?? "";
};
